import { useState } from "react";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";

export default function ContactUs() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const info = [
    { icon: "📧", title: "Email Us",      value: "support@taxvault.live",    sub: "We respond within 24 hours" },
    { icon: "🌐", title: "Website",       value: "www.taxvault.co.za",         sub: "Visit our platform" },
    { icon: "🇿🇦", title: "Based In",    value: "South Africa",               sub: "Serving the SA market" },
    { icon: "🕐", title: "Support Hours", value: "Mon–Fri: 8am – 6pm SAST",   sub: "Weekend support for Pro plans" },
  ];

  return (
    <div className="min-h-screen bg-luxury-bg">
      <PublicHeader />

      {/* Hero */}
      <section className="pt-28 pb-16 bg-luxury-surface border-b border-luxury-border text-center px-5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[250px] bg-luxury-gold/5 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-luxury-elevated border border-luxury-gold/30 rounded-full text-luxury-gold text-sm font-semibold mb-6">
            <span className="w-1.5 h-1.5 bg-luxury-gold rounded-full" />
            We're here to help
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-luxury-text mb-4">Contact Us</h1>
          <p className="text-luxury-secondary text-lg leading-relaxed">
            Have a question about TaxVault? We'd love to hear from you.
          </p>
        </div>
      </section>

      <section className="py-16 max-w-6xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

          {/* Info */}
          <div>
            <h2 className="text-2xl font-bold text-luxury-text mb-8">Get in Touch</h2>
            <div className="space-y-5">
              {info.map(item => (
                <div key={item.title} className="flex gap-4 p-4 bg-luxury-surface border border-luxury-border rounded-2xl hover:border-luxury-gold/20 transition-colors">
                  <div className="flex-shrink-0 w-11 h-11 bg-luxury-elevated border border-luxury-border rounded-xl flex items-center justify-center text-xl">
                    {item.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-luxury-text text-sm">{item.title}</h4>
                    <p className="text-luxury-gold font-semibold text-sm">{item.value}</p>
                    <p className="text-luxury-muted text-xs mt-0.5">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-5 bg-luxury-elevated border border-luxury-gold/20 rounded-2xl">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-luxury-gold">💡</span>
                <h3 className="font-bold text-luxury-text text-sm">Quick Tip</h3>
              </div>
              <p className="text-luxury-secondary text-sm leading-relaxed">
                For account issues or billing questions, please include your registered email address in your message so we can assist you faster.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="bg-luxury-surface border border-luxury-border rounded-2xl p-8">
            {submitted ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 mx-auto mb-5 bg-luxury-cta/10 border border-luxury-cta/30 rounded-2xl flex items-center justify-center text-3xl">
                  ✅
                </div>
                <h3 className="text-xl font-bold text-luxury-text mb-2">Message Sent!</h3>
                <p className="text-luxury-secondary text-sm mb-6">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                <button
                  onClick={() => { setSubmitted(false); setForm({ name: "", email: "", subject: "", message: "" }); }}
                  className="px-6 py-2.5 bg-luxury-gold hover:bg-luxury-gold-dim text-white rounded-xl font-bold text-sm transition-colors">
                  Send Another Message
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-bold text-luxury-text mb-6">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-luxury-secondary mb-1.5">Full Name</label>
                    <input type="text" required value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder="Your full name"
                      className="w-full px-4 py-3 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text placeholder-luxury-muted focus:outline-none focus:border-luxury-gold/50 focus:ring-2 focus:ring-luxury-gold/10 text-sm transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-luxury-secondary mb-1.5">Email Address</label>
                    <input type="email" required value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      placeholder="you@example.co.za"
                      className="w-full px-4 py-3 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text placeholder-luxury-muted focus:outline-none focus:border-luxury-gold/50 focus:ring-2 focus:ring-luxury-gold/10 text-sm transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-luxury-secondary mb-1.5">Subject</label>
                    <select value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text focus:outline-none focus:border-luxury-gold/50 focus:ring-2 focus:ring-luxury-gold/10 text-sm transition-all">
                      <option value="">Select a subject</option>
                      <option>General Inquiry</option>
                      <option>Account Support</option>
                      <option>Billing Question</option>
                      <option>Technical Issue</option>
                      <option>Partnership / Business</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-luxury-secondary mb-1.5">Message</label>
                    <textarea required rows={5} value={form.message}
                      onChange={e => setForm({ ...form, message: e.target.value })}
                      placeholder="Tell us how we can help you..."
                      className="w-full px-4 py-3 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text placeholder-luxury-muted focus:outline-none focus:border-luxury-gold/50 focus:ring-2 focus:ring-luxury-gold/10 text-sm transition-all resize-none" />
                  </div>
                  <button type="submit"
                    className="w-full py-3.5 bg-luxury-gold hover:bg-luxury-gold-dim text-white font-bold rounded-xl transition-all text-sm active:scale-95">
                    Send Message →
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
